/**
 * src/components/admin/UserManagementModal.tsx
 *
 * File-level:
 * - Modal component to manage application users (admin).
 * - Uses an explicit backend API origin (API_URL) to ensure requests reach the real server.
 *
 * Notes:
 * - This file forces admin requests to the backend origin so inserts are performed
 *   on the real DB, not accidentally on the frontend origin.
 */

/**
 * Component-level notes:
 * - All /api/admin/user-management fetch calls use API_URL so they go to the backend.
 * - Keep logs to help trace whether the request reached the backend.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { X, Plus, Edit, Check } from 'lucide-react';
import type { UserManagementRow } from '../../types/admin';
import { API_CONFIG } from '../../config/api.config';

/**
 * Props for UserManagementModal
 */
interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Helper: small debounce for quick UI use (not exported)
 * @param fn function
 * @param ms milliseconds
 */
function debounce<T extends (...args: any[]) => void>(fn: T, ms = 200) {
  let t: number | null = null;
  return (...args: any[]) => {
    if (t) window.clearTimeout(t);
    // @ts-ignore
    t = window.setTimeout(() => fn(...args), ms) as unknown as number;
  };
}

/**
 * UserManagementModal
 *
 * @description Admin modal to view/create/edit users.
 */
export default function UserManagementModal({ open, onClose }: Props): JSX.Element | null {
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(open);
  const ANIM_DURATION = 220;

  // Allow overriding backend URL via window.__BACKEND_ORIGIN__ for hosted setups
  const API_URL = API_CONFIG.getBackendOrigin();

  // data
  const [users, setUsers] = useState<UserManagementRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // create form
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ userId: '', displayName: '', password: '' });

  // inline password edits
  const [editingPasswordFor, setEditingPasswordFor] = useState<string | null>(null);
  const [newPasswordValue, setNewPasswordValue] = useState('');

  // pagination simple client-side
  const PAGE_SIZE = 8;
  const [page, setPage] = useState(1);

  // body scroll lock
  useEffect(() => {
    if (open) {
      setMounted(true);
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
      const t = window.setTimeout(() => setMounted(false), ANIM_DURATION + 10);
      return () => window.clearTimeout(t);
    }
    return;
  }, [open]);

  useEffect(() => {
    if (!mounted) return;
    const prev = document.body.style.overflow || '';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mounted]);

  /**
   * fetchUsers
   *
   * @description Fetch user list from backend. Falls back to small mock on failure so UI stays testable.
   */
  async function fetchUsers() {
    setError(null);
    setLoading(true);
    try {
      const url = `${API_URL}/api/admin/user-management`;
      console.log('[UserManagementModal] Fetching users from', url);
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache',
          'Accept': 'application/json',
        },
      });
      console.log('[UserManagementModal] Fetch response status:', res.status, res.statusText);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`${res.status} ${res.statusText} - ${txt}`);
      }
      const json = await res.json();
      console.log('[UserManagementModal] Fetched data:', json);
      // Accept { list: [] } or array
      const rawList = Array.isArray(json.list) ? json.list : Array.isArray(json) ? json : json.list || [];
      
      // Transform PascalCase properties to camelCase to match TypeScript interface
      const list: UserManagementRow[] = rawList.map((item: any) => ({
        userId: item.UserId || item.userId,
        displayName: item.DisplayName || item.displayName,
        isInactive: item.IsInactive ?? item.isInactive ?? false,
        createdAt: item.CreatedAt || item.createdAt,
        updatedAt: item.UpdatedAt || item.updatedAt,
      }));
      
      setUsers(list);
    } catch (err: any) {
      // fallback mock
      // eslint-disable-next-line no-console
      console.warn('[UserManagementModal] fetch failed, using fallback', err);
      setUsers([
        { userId: 'admin', displayName: 'Administrator', isInactive: false, createdAt: new Date().toISOString() },
        { userId: 'testuser', displayName: 'Test User', isInactive: true, createdAt: new Date().toISOString() },
      ]);
      setError(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) {
      fetchUsers();
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // simple filtered/paginated view (could be extended)
  const totalPages = Math.max(1, Math.ceil(users.length / PAGE_SIZE));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const visibleUsers = useMemo(() => {
    const s = (page - 1) * PAGE_SIZE;
    return users.slice(s, s + PAGE_SIZE);
  }, [users, page]);

  /**
   * createUser
   *
   * @description Call API to create new user then refresh.
   */
  async function createUser() {
    if (!newUser.userId.trim() || !newUser.displayName.trim() || !newUser.password) {
      setError('All fields are required');
      return;
    }
    setLoading(true);
    try {
      const url = `${API_URL}/api/admin/user-management`;
      console.log('[UserManagementModal] Creating user:', { userId: newUser.userId, displayName: newUser.displayName, passwordLength: newUser.password.length, url });
      const res = await fetch(url, {
        method: 'POST',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: newUser.userId.trim(),
          displayName: newUser.displayName.trim(),
          password: newUser.password,
        }),
      });
      console.log('[UserManagementModal] Response status:', res.status, res.statusText);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`${res.status} ${res.statusText} - ${txt}`);
      }
      const json = await res.json();
      console.log('[UserManagementModal] Success response:', json);
      // on success refresh list and reset form
      await fetchUsers();
      setNewUser({ userId: '', displayName: '', password: '' });
      setCreating(false);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[UserManagementModal] create failed', err);
      setError(err?.message ?? 'Create failed');
    } finally {
      setLoading(false);
    }
  }

  /**
   * updatePassword
   *
   * @description Update password for a userId via PATCH.
   */
  async function updatePassword(userId: string, password: string) {
    if (!password) return;
    setLoading(true);
    try {
      const url = `${API_URL}/api/admin/user-management/${encodeURIComponent(userId)}`;
      const res = await fetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`${res.status} ${res.statusText} - ${txt}`);
      }
      await fetchUsers();
      setEditingPasswordFor(null);
      setNewPasswordValue('');
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[UserManagementModal] update password failed', err);
      setError(err?.message ?? 'Update failed');
    } finally {
      setLoading(false);
    }
  }

  /**
   * toggleInactive
   *
   * @description Toggle the isInactive flag for a user (PATCH).
   */
  const toggleInactive = debounce(async (userId: string, newVal: boolean) => {
    try {
      const url = `${API_URL}/api/admin/user-management/${encodeURIComponent(userId)}`;
      const res = await fetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isInactive: newVal }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`${res.status} ${res.statusText} - ${txt}`);
      }
      await fetchUsers();
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[UserManagementModal] toggle inactive failed', err);
      setError(err?.message ?? 'Update failed');
    }
  }, 180);

  if (!mounted) return null;

  const backdropClass = visible ? 'opacity-100' : 'opacity-0 pointer-events-none';
  const modalClass = visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-3 scale-95';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="User management modal"
      className="fixed left-0 right-0 top-16 bottom-0 z-50 flex items-start justify-center px-4 py-6"
    >
      {/* Backdrop with fade */}
      <div
        onClick={onClose}
        aria-hidden="true"
        className={`fixed inset-0 transition-opacity duration-200 ease-out ${backdropClass}`}
        style={{
          backgroundImage: 'linear-gradient(180deg, rgba(2,6,23,0.7), rgba(7,16,26,0.7))',
          backdropFilter: 'blur(4px)',
        }}
      />

      {/* Modal container with enter/exit transform */}
      <div
        className={`relative w-full max-w-5xl rounded-lg overflow-hidden shadow-2xl ring-1 ring-black/10 bg-transparent transform transition-all duration-200 ease-out ${modalClass}`}
        style={{ transitionDuration: `${ANIM_DURATION}ms` }}
      >
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between gap-3" style={{ backgroundImage: 'linear-gradient(90deg,#4338ca,#ec4899)', color: 'white' }}>
          <div>
            <h3 className="text-lg font-semibold">User Management</h3>
            <p className="text-sm text-white/90">Create, edit passwords and toggle user active state</p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setCreating((s) => !s);
                setNewUser({ userId: '', displayName: '', password: '' });
              }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded bg-white/10 hover:bg-white/20 text-sm"
              title="Create new user"
            >
              <Plus className="w-4 h-4" />
              New User
            </button>

            <button onClick={onClose} aria-label="Close" className="p-2 rounded bg-white/10 hover:bg-white/20">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body: constrained with internal scroll */}
        <div className="bg-white p-6 max-h-[calc(100vh-12rem)] overflow-auto">
          {error && <div className="mb-3 text-sm text-red-600">{error}</div>}

          {/* Create form (toggle) */}
          {creating && (
            <div className="mb-4 border rounded p-4 bg-slate-50">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">User ID</label>
                  <input value={newUser.userId} onChange={(e) => setNewUser((s) => ({ ...s, userId: e.target.value }))} className="w-full px-3 py-2 border rounded" placeholder="unique user id" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Display Name</label>
                  <input value={newUser.displayName} onChange={(e) => setNewUser((s) => ({ ...s, displayName: e.target.value }))} className="w-full px-3 py-2 border rounded" placeholder="name shown in UI" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                  <input type="password" value={newUser.password} onChange={(e) => setNewUser((s) => ({ ...s, password: e.target.value }))} className="w-full px-3 py-2 border rounded" placeholder="temporary password" />
                </div>
              </div>

              <div className="mt-3 flex items-center gap-2">
                <button onClick={createUser} disabled={loading} className="px-3 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700">
                  {loading ? 'Creating...' : 'Create'}
                </button>
                <button onClick={() => setCreating(false)} className="px-3 py-2 rounded bg-slate-100">Cancel</button>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="overflow-hidden rounded-md border">
            <table className="w-full text-sm table-fixed">
              <colgroup>
                <col style={{ width: '14rem' }} />
                <col style={{ width: '12rem' }} />
                <col style={{ width: '1fr' }} />
                <col style={{ width: '7rem' }} />
                <col style={{ width: '6rem' }} />
              </colgroup>

              <thead className="bg-gradient-to-r from-violet-600 to-pink-500 text-white">
                <tr className="text-left">
                  <th className="py-3 pr-4 pl-4">User ID</th>
                  <th className="py-3 pr-4">Password</th>
                  <th className="py-3 pr-4">Display Name</th>
                  <th className="py-3 pr-4">Actions</th>
                  <th className="py-3 pr-4">Inactive</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="py-6 text-center text-slate-500">Loading...</td>
                  </tr>
                ) : visibleUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-6 text-center text-slate-500">No users found.</td>
                  </tr>
                ) : (
                  visibleUsers.map((u) => (
                    <tr key={u.userId} className="border-t last:border-b-0">
                      <td className="py-3 pr-4 pl-4 align-middle font-mono break-words max-w-[14rem]">{u.userId}</td>

                      {/* Password cell moved left (now second column) */}
                      <td className="py-3 pr-4 align-middle">
                        {editingPasswordFor === u.userId ? (
                          <input
                            type="password"
                            value={newPasswordValue}
                            onChange={(e) => setNewPasswordValue(e.target.value)}
                            className="w-full px-2 py-1 border rounded"
                            placeholder="new password"
                          />
                        ) : (
                          <div className="text-sm text-slate-600">••••••••</div>
                        )}
                      </td>

                      <td className="py-3 pr-4 align-middle break-words max-w-[20rem]">{u.displayName}</td>

                      {/* Actions: Edit / Save / Cancel */}
                      <td className="py-3 pr-4 align-middle">
                        {editingPasswordFor === u.userId ? (
                          <div className="flex items-center gap-2">
                            <button onClick={() => updatePassword(u.userId, newPasswordValue)} className="px-2 py-1 rounded bg-emerald-600 text-white text-sm inline-flex items-center gap-1">
                              <Check className="w-3 h-3 inline" /> Save
                            </button>
                            <button onClick={() => { setEditingPasswordFor(null); setNewPasswordValue(''); }} className="px-2 py-1 rounded bg-slate-100 text-sm">Cancel</button>
                          </div>
                        ) : (
                          <button onClick={() => { setEditingPasswordFor(u.userId); setNewPasswordValue(''); }} className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-sm inline-flex items-center gap-1">
                            <Edit className="w-3 h-3" /> Edit
                          </button>
                        )}
                      </td>

                      <td className="py-3 pr-4 align-middle">
                        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={Boolean(u.isInactive)}
                            onChange={(e) => toggleInactive(u.userId, e.target.checked)}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">{u.isInactive ? 'Yes' : 'No'}</span>
                        </label>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Footer pagination */}
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-slate-500">Showing {Math.max(1, (page - 1) * PAGE_SIZE + 1)}–{Math.min(users.length, page * PAGE_SIZE)} of {users.length} users.</div>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 rounded bg-slate-200 text-slate-700 text-sm hover:bg-slate-300 disabled:opacity-50">Previous</button>
              <span className="text-sm font-medium text-slate-700">Page {page} of {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 rounded bg-slate-200 text-slate-700 text-sm hover:bg-slate-300 disabled:opacity-50">Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}